import { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarPlus, X, PawPrint, Dog, Cat, Heart, ShieldCheck, Stethoscope, MapPin, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const placeholderImg = '/sem-foto.jpg';

const HERO_DOG = 'https://images.unsplash.com/photo-1785952473255-96900d71547c?q=80&w=900&fm=jpg&fit=crop';
const HERO_CAT = 'https://images.unsplash.com/photo-1723028075357-a2ce23ce6505?q=80&w=900&fm=jpg&fit=crop';

const pawDecor = [
  { top: '10%', left: '6%', size: 38, rotate: -18, o: 0.08 },
  { top: '70%', left: '3%', size: 30, rotate: 22, o: 0.07 },
  { top: '18%', left: '46%', size: 26, rotate: -30, o: 0.06 },
  { top: '80%', left: '44%', size: 34, rotate: 14, o: 0.07 },
  { top: '8%', left: '88%', size: 40, rotate: 20, o: 0.08 },
  { top: '64%', left: '93%', size: 28, rotate: -12, o: 0.07 },
];

export default function PublicPage() {
  const [animais, setAnimais] = useState([]);
  const [filtro, setFiltro] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [showVisita, setShowVisita] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showRgpd, setShowRgpd] = useState(false);
  const [visitaForm, setVisitaForm] = useState({ animal_id: '', nome: '', email: '', telefone: '', data_preferida: '' });
  const [visitaMsg, setVisitaMsg] = useState('');

  useEffect(() => {
    const especie = filtro === 'todos' ? '' : `?especie=${filtro}`;
    axios.get(`${API}/animais/publico/adocao${especie}`)
      .then(r => setAnimais(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filtro]);

  const enviarPedido = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pedidos-visita`, visitaForm);
      setVisitaMsg('Pedido enviado com sucesso!');
      setVisitaForm({ animal_id: '', nome: '', email: '', telefone: '', data_preferida: '' });
    } catch { setVisitaMsg('Erro ao enviar pedido.'); }
  };

  const abrirVisita = (animalId = '') => {
    setVisitaForm(f => ({ ...f, animal_id: animalId }));
    setVisitaMsg('');
    setShowVisita(true);
  };

  const fotoDe = (a) => {
    if (a.fotos?.length > 0) {
      const u = a.fotos[a.foto_capa_index || 0];
      return (u && (u.startsWith('http') || u.startsWith('data:'))) ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
    }
    return placeholderImg;
  };

  return (
    <div data-testid="public-page" className="-mt-6 -mx-4 sm:-mx-6 lg:-mx-8 -mb-6 overflow-x-clip" style={{ background: '#F8F6F0' }}>
      <style>{`
        @keyframes pubFadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pubFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .pub-fade-up { animation: pubFadeUp 0.7s ease-out both; }
        .pub-fade-up-1 { animation: pubFadeUp 0.7s 0.1s ease-out both; }
        .pub-fade-up-2 { animation: pubFadeUp 0.7s 0.2s ease-out both; }
        .pub-fade-up-3 { animation: pubFadeUp 0.7s 0.3s ease-out both; }
        .pub-float { animation: pubFloat 5s ease-in-out infinite; }
        .pub-float-slow { animation: pubFloat 7s 1s ease-in-out infinite; }
      `}</style>

      {/* ===== HERO ===== */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(150deg, #F8F6F0 0%, #EEF3F8 55%, #F3F0E8 100%)' }}>
        {pawDecor.map((d, i) => (
          <PawPrint key={i} className="absolute text-primary pointer-events-none" style={{ top: d.top, left: d.left, width: d.size, height: d.size, opacity: d.o, transform: `rotate(${d.rotate}deg)` }} />
        ))}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-16 lg:pb-16 grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
          {/* Texto */}
          <div className="text-left">
            <h1 className="pub-fade-up-1 font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-800 leading-[1.08] mb-5">
              Animais para{' '}
              <span className="relative inline-block text-primary">
                Adoção
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 180 12" fill="none" preserveAspectRatio="none" style={{ height: 10 }}>
                  <path d="M3 9C45 3 135 3 177 9" stroke="#E07A5F" strokeWidth="5" strokeLinecap="round" opacity="0.65" />
                </svg>
              </span>
            </h1>
            <p className="pub-fade-up-2 text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed mb-7">
              Adotar é um ato de amor que muda duas vidas. Consulte os animais disponíveis para adoção no Centro de Recolha Oficial de Vila Franca de Xira.
            </p>
            <div className="pub-fade-up-3 flex flex-wrap items-center gap-4">
              <button onClick={() => abrirVisita()} data-testid="agendar-visita-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                style={{ background: '#E07A5F' }}>
                <CalendarPlus className="w-4 h-4" /> Agendar Visita
              </button>
              <div className="flex items-center gap-2 text-sm text-slate-500 font-medium">
                <span className="inline-flex items-center gap-1.5"><Stethoscope className="w-4 h-4 text-[#81B29A]" /> Cuidados médicos</span>
                <span className="text-slate-300">•</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#81B29A]" /> Adoção responsável</span>
              </div>
            </div>
          </div>
          {/* Colagem visual */}
          <div className="relative h-[320px] sm:h-[380px] lg:h-[420px] select-none" aria-hidden="true">
            <div className="pub-float absolute top-0 right-[6%] w-[58%] max-w-[300px] aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white rotate-[3deg]">
              <img src={HERO_DOG} alt="Cão para adoção" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="pub-float-slow absolute bottom-0 left-[4%] w-[46%] max-w-[240px] aspect-square rounded-[2rem] overflow-hidden shadow-xl border-4 border-white -rotate-[4deg]">
              <img src={HERO_CAT} alt="Gato para adoção" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute top-[8%] left-[14%] w-20 h-20 sm:w-24 sm:h-24 rounded-3xl overflow-hidden shadow-lg border-4 border-white bg-white rotate-[-6deg] p-1.5">
              <img src="/logo.jpg" alt="AnimaLog" className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
            </div>
            <div className="absolute bottom-[14%] right-[2%] bg-white rounded-2xl shadow-lg px-4 py-2.5 flex items-center gap-2 rotate-[2deg]">
              <Heart className="w-4 h-4 text-[#E07A5F] fill-[#E07A5F]" />
              <span className="text-xs font-bold text-slate-700">Adotar é amar</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-b from-transparent to-[#F8F6F0] pointer-events-none" />
      </div>

      {/* ===== LISTAGEM ===== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-800 flex items-center gap-2">
            Disponíveis para Adoção
            {!loading && animais.length > 0 && <span className="text-sm font-bold bg-primary/10 text-primary rounded-full px-3 py-1">{animais.length}</span>}
          </h2>
          <div className="flex gap-2">
            {['todos', 'canina', 'felina'].map(f => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFiltro(f)}
                className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm transition-all duration-200 ${filtro === f ? 'bg-primary text-primary-foreground font-semibold shadow-md' : 'bg-white border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 hover:border-slate-300'}`}>
                {f === 'canina' && <Dog className="w-4 h-4" />}
                {f === 'felina' && <Cat className="w-4 h-4" />}
                {f === 'todos' ? 'Todos' : f === 'canina' ? 'Canina' : 'Felina'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">A carregar...</div>
        ) : animais.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 shadow-sm" data-testid="empty-animals-state">
            <div className="w-16 h-16 rounded-full bg-[#F2CC8F]/25 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-7 h-7 text-[#E07A5F]" />
            </div>
            <p className="text-slate-600 font-medium">Nenhum animal disponível para adoção no momento.</p>
            <p className="text-slate-400 text-sm mt-1">Volte em breve — há sempre novos amigos à procura de casa.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-7">
            {animais.map((a, idx) => (
              <div key={a.id} data-testid={`public-animal-${a.id}`}
                className="group bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 ease-out flex flex-col pub-fade-up"
                style={{ animationDelay: `${Math.min(idx * 0.06, 0.5)}s` }}>
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <img src={fotoDe(a)} alt={a.nome} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                    onError={e => { e.target.src = placeholderImg; }} />
                  {a.esterilizado && (
                    <span className="absolute top-3 right-3 bg-emerald-600/90 text-white backdrop-blur-md text-[11px] font-bold px-3 py-1 rounded-full shadow-sm">Esterilizado</span>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading font-extrabold text-xl text-slate-800 group-hover:text-primary transition-colors">{a.nome}</h3>
                    {a.especie === 'canina' ? <Dog className="w-5 h-5 text-slate-300 mt-1" /> : <Cat className="w-5 h-5 text-slate-300 mt-1" />}
                  </div>
                  <p className="text-sm text-slate-500 font-medium capitalize mt-0.5">{a.especie} {a.sexo ? `· ${a.sexo}` : ''}{a.idade ? ` · ${a.idade}` : ''}</p>
                  <button onClick={() => abrirVisita(a.id)} data-testid={`visitar-${a.id}`}
                    className="mt-4 w-full bg-slate-50 group-hover:bg-primary group-hover:text-white text-slate-700 text-sm font-semibold py-2.5 px-4 rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 border border-slate-200/80 group-hover:border-primary">
                    Conhecer {a.nome} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== BANNER CTA ===== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <div className="relative overflow-hidden rounded-[2rem] px-6 sm:px-12 py-7 sm:py-8 text-center shadow-xl" style={{ background: 'linear-gradient(135deg, #2C5282 0%, #1E3A5F 100%)' }}>
          <PawPrint className="absolute -top-4 -left-4 w-28 h-28 text-white/10 rotate-12" />
          <PawPrint className="absolute -bottom-6 -right-2 w-32 h-32 text-white/10 -rotate-12" />
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-white mb-2">Pronto para mudar uma vida?</h2>
          <p className="text-blue-100 text-sm sm:text-base max-w-2xl mx-auto mb-5">Agende uma visita ao centro e venha conhecer o seu novo melhor amigo pessoalmente.</p>
          <button onClick={() => abrirVisita()} data-testid="agendar-visita-banner-btn"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold text-slate-800 bg-[#F2CC8F] hover:bg-[#efc277] hover:-translate-y-0.5 shadow-lg transition-all duration-200">
            <CalendarPlus className="w-4 h-4" /> Agendar Visita
          </button>
        </div>
      </div>

      {/* Agendar Visita Modal */}
      {showVisita && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-md m-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg">Agendar Visita</h3>
              <button onClick={() => { setShowVisita(false); setVisitaMsg(''); }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={enviarPedido} className="p-4 space-y-3">
              <div><label className="text-sm font-medium block mb-1">Animal</label>
                <select value={visitaForm.animal_id} onChange={e => setVisitaForm({ ...visitaForm, animal_id: e.target.value })} required className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
                  <option value="">-- Selecionar Animal --</option>
                  {animais.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                </select>
              </div>
              <div><label className="text-sm font-medium block mb-1">Nome</label><input value={visitaForm.nome} onChange={e => setVisitaForm({ ...visitaForm, nome: e.target.value })} required className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></div>
              <div><label className="text-sm font-medium block mb-1">Email</label><input type="email" value={visitaForm.email} onChange={e => setVisitaForm({ ...visitaForm, email: e.target.value })} required className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></div>
              <div><label className="text-sm font-medium block mb-1">Telemóvel</label><input type="tel" value={visitaForm.telefone} onChange={e => setVisitaForm({ ...visitaForm, telefone: e.target.value })} pattern="[0-9]{9}" required className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" placeholder="912345678" /></div>
              <div><label className="text-sm font-medium block mb-1">Data da Visita</label><input type="date" value={visitaForm.data_preferida} onChange={e => setVisitaForm({ ...visitaForm, data_preferida: e.target.value })} required className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></div>
              {visitaMsg && <p className="text-sm text-green-600">{visitaMsg}</p>}
              <div className="bg-muted/50 rounded-lg p-2.5">
                <p className="text-[11px] text-muted-foreground leading-relaxed">Ao submeter este formulário, consente o tratamento dos seus dados pessoais nos termos do RGPD (Regulamento UE 2016/679). Os dados serão utilizados exclusivamente para agendamento de visita e não serão partilhados com terceiros.</p>
              </div>
              <button type="submit" className="w-full text-white rounded-full py-2.5 text-sm font-bold hover:brightness-110 transition-all" style={{ background: '#E07A5F' }}>Enviar Pedido</button>
            </form>
          </div>
        </div>
      )}

      {/* Sobre Modal */}
      {showInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-md m-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg">Sobre a AnimaLog</h3>
              <button onClick={() => setShowInfo(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4">
              <p className="text-sm text-foreground leading-relaxed">A <strong>AnimaLog</strong> é uma aplicação desenvolvida para facilitar a gestão de animais nos Centros de Recolha Oficial. Aqui pode consultar os animais disponíveis para adoção, agendar uma visita e aceder a informações úteis.</p>
            </div>
            <div className="p-4 border-t border-border"><button onClick={() => setShowInfo(false)} className="w-full bg-muted text-foreground rounded-full py-2 text-sm font-medium hover:bg-border transition-all">Fechar</button></div>
          </div>
        </div>
      )}

      {/* RGPD Modal */}
      {showRgpd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-lg m-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg">Política de Privacidade e Proteção de Dados</h3>
              <button onClick={() => setShowRgpd(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-3">
              <p className="text-sm text-foreground leading-relaxed">Este website é gerido pelo Centro de Recolha Oficial de Vila Franca de Xira e respeita o Regulamento Geral sobre a Proteção de Dados (RGPD — Regulamento UE 2016/679) e a legislação nacional aplicável (Lei n.º 58/2019, de 8 de agosto).</p>
              <p className="text-sm text-foreground leading-relaxed"><strong>Dados recolhidos:</strong> Os dados pessoais recolhidos através do formulário de agendamento de visita (nome, email e telemóvel) são utilizados exclusivamente para a gestão de pedidos de visita ao Centro de Recolha. Os dados não são cedidos a terceiros nem utilizados para fins de marketing.</p>
              <p className="text-sm text-foreground leading-relaxed"><strong>Direitos do titular:</strong> Nos termos do RGPD, tem o direito de aceder, retificar, apagar ou limitar o tratamento dos seus dados pessoais, bem como o direito à portabilidade dos dados e de apresentar reclamação junto da CNPD (Comissão Nacional de Proteção de Dados).</p>
              <p className="text-sm text-foreground leading-relaxed"><strong>Cookies:</strong> Este website utiliza apenas cookies estritamente necessários ao funcionamento da plataforma. Não são utilizados cookies de rastreamento ou publicitários.</p>
              <p className="text-sm text-foreground leading-relaxed"><strong>Responsável pelo tratamento:</strong> Centro de Recolha Oficial de Vila Franca de Xira. Para exercer os seus direitos ou esclarecer dúvidas sobre proteção de dados, contacte o responsável pelo tratamento de dados.</p>
            </div>
            <div className="p-4 border-t border-border"><button onClick={() => setShowRgpd(false)} className="w-full bg-muted text-foreground rounded-full py-2 text-sm font-medium hover:bg-border transition-all">Fechar</button></div>
          </div>
        </div>
      )}

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#0F172A] text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid sm:grid-cols-3 gap-8 items-start">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white p-0.5">
                <img src="/logo.jpg" alt="AnimaLog" className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
              </div>
              <span className="font-heading font-extrabold text-lg text-white">AnimaLog</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">Gestão moderna e transparente do Centro de Recolha Oficial de Vila Franca de Xira.</p>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Informações</h4>
            <div className="flex flex-col items-start gap-2">
              <button onClick={() => setShowInfo(true)} data-testid="link-sobre" className="text-sm text-slate-300 hover:text-white transition-colors">Sobre a AnimaLog</button>
              <button onClick={() => setShowRgpd(true)} data-testid="link-privacidade" className="text-sm text-slate-300 hover:text-white transition-colors">Política de Privacidade</button>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Visite-nos</h4>
            <a href="https://maps.app.goo.gl/sYURfjAzqLwHVTGM8" target="_blank" rel="noopener noreferrer" data-testid="link-maps"
              className="text-sm text-slate-400 hover:text-white leading-relaxed flex items-start gap-2 transition-colors underline-offset-2 hover:underline">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#E07A5F]" /> Centro de Recolha Oficial de Vila Franca de Xira
            </a>
            <button onClick={() => abrirVisita()} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#F2CC8F] hover:text-white transition-colors">
              <CalendarPlus className="w-4 h-4" /> Agendar uma visita
            </button>
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-center gap-1.5 text-xs text-slate-500">
            Feito com <Heart className="w-3.5 h-3.5 text-[#E07A5F] fill-[#E07A5F]" /> pelos que cuidam deles todos os dias · AnimaLog © {new Date().getFullYear()}
          </div>
        </div>
      </footer>
    </div>
  );
}
